/**
 * chatCompletion.js
 *
 * Provides two functions for interacting with the chat-completion Lambda proxy:
 *   - getChatCompletion     – standard (non-streaming) request/response
 *   - getStreamingChatCompletion – server-sent events (SSE) streaming
 *
 * Both functions are provider-agnostic: pass any supported provider
 * identifier (e.g. 'GEMINI', 'OPENAI', 'ANTHROPIC') and model name.
 *
 * The Lambda endpoint URL is read from the VITE_AWS_LAMBDA_CHAT_COMPLETION_URL
 * environment variable.
 */

import { callLambdaFunction } from '../aiClient';

/** Lambda endpoint for chat completions (set via .env) */
const CHAT_COMPLETION_ENDPOINT = import.meta.env?.VITE_AWS_LAMBDA_CHAT_COMPLETION_URL;

// ─── Non-streaming ────────────────────────────────────────────────────────────

/**
 * Sends a non-streaming chat completion request to the Lambda proxy.
 *
 * @param {string}   provider  - Provider identifier (e.g. 'GEMINI', 'OPENAI')
 * @param {string}   model     - Model name (e.g. 'gemini/gemini-2.5-flash')
 * @param {Array}    messages  - OpenAI-style messages array
 * @param {object}  [options]  - Additional parameters (temperature, max_tokens, …)
 * @returns {Promise<object>}  Full SDK-style response object
 */
export async function getChatCompletion(provider, model, messages, options = {}) {
  const payload = {
    provider,
    model,
    messages,
    stream: false,
    parameters: options,
  };

  // Returns the full Lambda response for maximum flexibility
  return callLambdaFunction(CHAT_COMPLETION_ENDPOINT, payload);
}

// ─── Streaming ────────────────────────────────────────────────────────────────

/**
 * Sends a streaming chat completion request and processes SSE chunks.
 *
 * @param {string}   provider   - Provider identifier
 * @param {string}   model      - Model name
 * @param {Array}    messages   - OpenAI-style messages array
 * @param {Function} onChunk    - Called with each streamed chunk object
 * @param {Function} onComplete - Called when the stream finishes
 * @param {Function} onError    - Called with an Error if the stream fails
 * @param {object}  [options]   - Additional parameters
 */
export async function getStreamingChatCompletion(
  provider,
  model,
  messages,
  onChunk,
  onComplete,
  onError,
  options = {}
) {
  const payload = {
    provider,
    model,
    messages,
    stream: true,
    parameters: options,
  };

  try {
    const response = await fetch(CHAT_COMPLETION_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response?.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response?.body?.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    // Read SSE stream until done
    while (true) {
      const { done, value } = await reader?.read();
      if (done) break;

      buffer += decoder?.decode(value, { stream: true });

      // Split on newlines; keep any incomplete line in the buffer
      const lines = buffer?.split('\n');
      buffer = lines?.pop() || '';

      for (const line of lines) {
        if (!line?.startsWith('data: ')) continue;

        try {
          const data = JSON.parse(line?.slice(6));

          if (data?.type === 'chunk' && data?.chunk) {
            onChunk(data?.chunk);
          } else if (data?.type === 'done') {
            onComplete();
          } else if (data?.type === 'error') {
            console.error('Lambda streaming error:', { error: data?.error, details: data?.details });
            onError(new Error(data.error));
          }
        } catch {
          // Skip malformed JSON lines — common at stream boundaries
        }
      }
    }
  } catch (error) {
    console.error('Streaming chat completion error:', error);
    onError(error);
  }
}